import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { Clipboard, ExternalLink, Eye } from 'lucide-react';
import {
  fetchStudentClassworkAssignments,
  fetchStudentAssignmentDetail,
  submitStudentAssignment,
  openStudentOnlyofficeSession,
  finalizeStudentOnlyofficeSubmission,
  uploadStudentPenpaperPage,
  fetchStudentPenpaperPages,
  deleteStudentPenpaperPage,
  finalizeStudentPenpaperSubmission
} from '../../lib/classworkApi';
import StudentTiptapEditorPane from '../../components/classwork/StudentTiptapEditorPane';

function formatDateTime(value: string | null | undefined) {
  if (!value) return null;
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleString();
}

function deadlineMeta(dueAt: string | null | undefined, latestAt: string | null | undefined) {
  const deadline = latestAt || dueAt;
  if (!deadline) {
    return {
      label: 'Keine Frist',
      short: 'Keine Frist',
      isOverdue: false,
      toneClass: 'text-gray-600'
    };
  }
  const dt = new Date(deadline);
  if (Number.isNaN(dt.getTime())) {
    return {
      label: 'Frist unbekannt',
      short: 'Frist unbekannt',
      isOverdue: false,
      toneClass: 'text-gray-600'
    };
  }
  const isOverdue = dt < new Date();
  return {
    label: `${isOverdue ? 'Frist abgelaufen' : 'Abgabe bis'}: ${dt.toLocaleString()}`,
    short: `${isOverdue ? 'Abgelaufen' : 'Bis'} ${dt.toLocaleDateString()}`,
    isOverdue,
    toneClass: isOverdue ? 'text-red-600' : 'text-emerald-700'
  };
}

function stateUi(stateRaw: string | null | undefined) {
  const state = String(stateRaw || '').toLowerCase();
  if (['published'].includes(state)) return { label: 'Bewertung veröffentlicht', badge: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
  if (['graded'].includes(state)) return { label: 'Bewertet', badge: 'bg-blue-100 text-blue-800 border-blue-200' };
  if (['submitted', 'locked'].includes(state)) return { label: 'Abgegeben', badge: 'bg-indigo-100 text-indigo-800 border-indigo-200' };
  if (['opened', 'started', 'resumed', 'in_progress'].includes(state)) return { label: 'In Bearbeitung', badge: 'bg-amber-100 text-amber-800 border-amber-200' };
  if (['assigned'].includes(state)) return { label: 'Noch nicht begonnen', badge: 'bg-slate-100 text-slate-700 border-slate-200' };
  if (['expired'].includes(state)) return { label: 'Abgelaufen', badge: 'bg-rose-100 text-rose-800 border-rose-200' };
  return { label: 'Noch nicht begonnen', badge: 'bg-slate-100 text-slate-700 border-slate-200' };
}

function formatPublishedResult(assignment: any) {
  const hasGrade = assignment?.result_grade_value && String(assignment.result_grade_value).trim() !== '';
  const hasPoints = assignment?.result_points_achieved != null && assignment?.result_points_max != null;
  const hasPercent = assignment?.result_score_percent != null;
  if (!hasGrade && !hasPoints && !hasPercent) return null;
  const parts: string[] = [];
  if (hasGrade) parts.push(`Note ${assignment.result_grade_value}`);
  if (hasPoints) parts.push(`${Number(assignment.result_points_achieved)} / ${Number(assignment.result_points_max)} Punkte`);
  if (hasPercent) parts.push(`${Math.round(Number(assignment.result_score_percent))}%`);
  return parts.join(' · ');
}

function isExternalMaterial(material: any) {
  return String(material?.file_source || '').toLowerCase() === 'external' && String(material?.external_url || '').trim() !== '';
}

function isImageMaterial(material: any) {
  const mime = String(material?.mime_type || '').toLowerCase();
  const name = String(material?.original_name || '').toLowerCase();
  return mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(name);
}

function isPdfMaterial(material: any) {
  const mime = String(material?.mime_type || '').toLowerCase();
  const name = String(material?.original_name || '').toLowerCase();
  return mime.includes('pdf') || name.endsWith('.pdf');
}

function isTextMaterial(material: any) {
  if (isExternalMaterial(material)) return false;
  const mime = String(material?.mime_type || '').toLowerCase();
  const name = String(material?.original_name || '').toLowerCase();
  return mime.startsWith('text/') || /\.(txt|md|csv|json|xml|html?)$/.test(name);
}

const miniActionBtnClass =
  'inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-900 hover:text-white transition-colors';

function parseOnlyofficeError(metadata: unknown): { status?: string; lastError?: string; lastErrorAt?: string } | null {
  if (!metadata) return null;
  let obj: any = metadata;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return null;
    }
  }
  const oo = obj?.onlyoffice;
  if (!oo || typeof oo !== 'object') return null;
  return {
    status: oo.callbackStatus ? String(oo.callbackStatus) : undefined,
    lastError: oo.lastError ? String(oo.lastError) : undefined,
    lastErrorAt: oo.lastErrorAt ? String(oo.lastErrorAt) : undefined
  };
}

function parseOnlyofficeRetry(metadata: unknown): { status?: string; retryRequestedAt?: string } | null {
  if (!metadata) return null;
  let obj: any = metadata;
  if (typeof obj === 'string') {
    try {
      obj = JSON.parse(obj);
    } catch {
      return null;
    }
  }
  const oo = obj?.onlyoffice;
  if (!oo || typeof oo !== 'object') return null;
  return {
    status: oo.callbackStatus ? String(oo.callbackStatus) : undefined,
    retryRequestedAt: oo.retryRequestedAt ? String(oo.retryRequestedAt) : undefined
  };
}

function isTeacherOnlyMaterial(material: any) {
  const materialType = String(material?.material_type || material?.materialType || '').toLowerCase();
  return materialType === 'erwartungshorizont' || materialType === 'musterloesung';
}

export default function StudentClassworkPage() {
  const [searchParams] = useSearchParams();
  const urlSebToken = (searchParams.get('sebToken') || '').trim();

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [sebTokenByAssignment, setSebTokenByAssignment] = useState<Record<number, string>>({});
  const [uploadingFor, setUploadingFor] = useState<number | null>(null);
  const [openingEditorFor, setOpeningEditorFor] = useState<number | null>(null);
  const [finalizingEditorFor, setFinalizingEditorFor] = useState<number | null>(null);
  const [workspaceAssignmentId, setWorkspaceAssignmentId] = useState<number | null>(null);
  const [workspaceMode, setWorkspaceMode] = useState<'onlyoffice' | 'tiptap'>('onlyoffice');
  const [workspaceSession, setWorkspaceSession] = useState<any>(null);
  const [isDistractionFree, setIsDistractionFree] = useState(false);
  const [selectedMaterialIdByAssignment, setSelectedMaterialIdByAssignment] = useState<Record<number, number | null>>({});
  const [materialPreviewText, setMaterialPreviewText] = useState<string>('');
  const [materialPreviewLoading, setMaterialPreviewLoading] = useState(false);
  const [materialPreviewError, setMaterialPreviewError] = useState<string | null>(null);
  const [copyingMaterialId, setCopyingMaterialId] = useState<number | null>(null);
  const [submittingTiptapFor, setSubmittingTiptapFor] = useState<number | null>(null);
  const [uploadingPenpaperFor, setUploadingPenpaperFor] = useState<number | null>(null);
  const [finalizingPenpaperFor, setFinalizingPenpaperFor] = useState<number | null>(null);
  const [penpaperPagesByAssignment, setPenpaperPagesByAssignment] = useState<Record<number, any[]>>({});
  const [splitRatio, setSplitRatio] = useState<number>(0.5);
  const [workspaceLoading, setWorkspaceLoading] = useState<boolean>(true);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const splitWrapRef = useRef<HTMLDivElement | null>(null);

  const assignmentsQuery = useQuery(['student-classwork-assignments'], fetchStudentClassworkAssignments);

  const detailQuery = useQuery(
    ['student-classwork-detail', expandedId],
    () => fetchStudentAssignmentDetail(expandedId as number),
    { enabled: !!expandedId }
  );

  useEffect(() => {
    if (!expandedId) return;
    void refreshPenpaperPages(expandedId);
  }, [expandedId]);

  const getSebToken = (assignmentId: number) =>
    (sebTokenByAssignment[assignmentId] ?? '').trim() || urlSebToken;

  const canUpload = (row: any) => {
    const state = String(row.state || '');
    if (['graded', 'published', 'expired'].includes(state)) return false;
    if (state === 'submitted') {
      const max = Math.max(1, Number(row.max_attempts) || 1);
      const used = Number(row.attempt_count) || 0;
      if (max <= 1) return false;
      if (used >= max) return false;
    }
    return true;
  };

  const studentUploadModeOf = (row: any): 'all_common' | 'images_pdf' | 'images_only' | 'pdf_only' => {
    const mode = String(row?.student_upload_mode || '').trim().toLowerCase();
    if (mode === 'images_pdf' || mode === 'images_only' || mode === 'pdf_only') return mode;
    return 'all_common';
  };

  const uploadAcceptForMode = (mode: 'all_common' | 'images_pdf' | 'images_only' | 'pdf_only') => {
    if (mode === 'images_only') return 'image/*';
    if (mode === 'pdf_only') return 'application/pdf,.pdf';
    if (mode === 'images_pdf') return 'image/*,application/pdf,.pdf';
    return '.pdf,image/*,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.odt,.ods,.odp,.rtf,.txt,.csv';
  };

  const uploadModeLabel = (mode: 'all_common' | 'images_pdf' | 'images_only' | 'pdf_only') => {
    if (mode === 'images_only') return 'nur Bilder';
    if (mode === 'pdf_only') return 'nur PDF';
    if (mode === 'images_pdf') return 'Bilder + PDF';
    return 'alle gängigen Formate';
  };

  const list = useMemo(() => assignmentsQuery.data || [], [assignmentsQuery.data]);
  const nextSplitAssignment = useMemo(
    () =>
      list.find(
        (row: any) => (Number(row.onlyoffice_enabled) || Number(row.tiptap_enabled)) && canUpload(row)
      ) || null,
    [list]
  );
  const activeWorkspaceAssignment = useMemo(
    () => list.find((row: any) => Number(row.assignment_id) === Number(workspaceAssignmentId)) || null,
    [list, workspaceAssignmentId]
  );
  const workspaceEditorDivId = workspaceAssignmentId
    ? `classwork-student-onlyoffice-split-${workspaceAssignmentId}`
    : 'classwork-student-onlyoffice-split';
  const workspaceMaterials = useMemo(
    () =>
      workspaceAssignmentId
        ? (detailQuery.data?.materials || []).filter((m: any) => !isTeacherOnlyMaterial(m))
        : [],
    [workspaceAssignmentId, detailQuery.data?.materials]
  );
  const selectedWorkspaceMaterial = useMemo(() => {
    if (!workspaceAssignmentId) return null;
    const pickedId = selectedMaterialIdByAssignment[workspaceAssignmentId];
    return workspaceMaterials.find((m: any) => Number(m.id) === Number(pickedId)) || workspaceMaterials[0] || null;
  }, [workspaceAssignmentId, workspaceMaterials, selectedMaterialIdByAssignment]);

  const refreshPenpaperPages = async (assignmentId: number) => {
    try {
      const data = await fetchStudentPenpaperPages(assignmentId);
      setPenpaperPagesByAssignment((prev) => ({ ...prev, [assignmentId]: Array.isArray(data?.pages) ? data.pages : [] }));
    } catch {
      // ignore background refresh errors in UI helper
    }
  };

  useEffect(() => {
    if (!workspaceAssignmentId || !workspaceSession) return;
    setWorkspaceLoading(true);
    setWorkspaceError(null);
    const docServerUrl = String(workspaceSession.docServerUrl || '').replace(/\/$/, '');
    if (!docServerUrl) {
      setWorkspaceError('OnlyOffice-Server ist nicht verfügbar');
      setWorkspaceLoading(false);
      return;
    }
    const scriptUrl = `${docServerUrl}/web-apps/apps/api/documents/api.js`;
    const initEditor = () => {
      const DocsAPI = (window as unknown as { DocsAPI?: { DocEditor: new (id: string, config: object) => void } }).DocsAPI;
      if (!DocsAPI?.DocEditor) {
        setWorkspaceError('OnlyOffice API konnte nicht geladen werden');
        setWorkspaceLoading(false);
        return;
      }
      try {
        const cfg: Record<string, unknown> = {
          documentType: workspaceSession.documentType,
          document: workspaceSession.document,
          editorConfig: workspaceSession.editorConfig
        };
        if (workspaceSession.token) cfg.token = workspaceSession.token;
        new DocsAPI.DocEditor(workspaceEditorDivId, cfg);
        setWorkspaceLoading(false);
      } catch {
        setWorkspaceError('Editor konnte nicht gestartet werden');
        setWorkspaceLoading(false);
      }
    };
    const existing = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existing) {
      initEditor();
    } else {
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.onload = initEditor;
      script.onerror = () => {
        setWorkspaceError('OnlyOffice Script konnte nicht geladen werden');
        setWorkspaceLoading(false);
      };
      document.head.appendChild(script);
    }
    return () => {
      const docApi = (window as unknown as { DocsAPI?: { DocEditor?: (id: string) => { destroyEditor?: () => void } } }).DocsAPI;
      if (docApi?.DocEditor && typeof docApi.DocEditor === 'function') {
        try {
          const ed = docApi.DocEditor(workspaceEditorDivId);
          if (ed?.destroyEditor) ed.destroyEditor();
        } catch (_) {}
      }
    };
  }, [workspaceAssignmentId, workspaceSession, workspaceEditorDivId]);

  useEffect(() => {
    if (!isDistractionFree) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isDistractionFree]);

  useEffect(() => {
    if (!workspaceAssignmentId) return;
    if (!workspaceMaterials.length) return;
    const picked = selectedMaterialIdByAssignment[workspaceAssignmentId];
    if (picked && workspaceMaterials.some((m: any) => Number(m.id) === Number(picked))) return;
    setSelectedMaterialIdByAssignment((prev) => ({ ...prev, [workspaceAssignmentId]: Number(workspaceMaterials[0].id) }));
  }, [workspaceAssignmentId, workspaceMaterials, selectedMaterialIdByAssignment]);

  useEffect(() => {
    const material = selectedWorkspaceMaterial;
    if (!material || !activeWorkspaceAssignment) {
      setMaterialPreviewText('');
      setMaterialPreviewError(null);
      setMaterialPreviewLoading(false);
      return;
    }
    if (!isTextMaterial(material)) {
      setMaterialPreviewText('');
      setMaterialPreviewError(null);
      setMaterialPreviewLoading(false);
      return;
    }
    const classworkId = Number(activeWorkspaceAssignment.classwork_id);
    const materialId = Number(material.id);
    let cancelled = false;
    setMaterialPreviewLoading(true);
    setMaterialPreviewError(null);
    fetch(`/api/student/classwork/${classworkId}/materials/${materialId}/view`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`http_${res.status}`);
        return res.text();
      })
      .then((txt) => {
        if (cancelled) return;
        setMaterialPreviewText(txt || '');
      })
      .catch(() => {
        if (cancelled) return;
        setMaterialPreviewText('');
        setMaterialPreviewError('Text konnte nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setMaterialPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedWorkspaceMaterial, activeWorkspaceAssignment]);

  const splitPresets = [
    { label: '0:1', ratio: 0 },
    { label: '1:3', ratio: 0.25 },
    { label: '1:2', ratio: 1 / 3 },
    { label: '1:1', ratio: 0.5 },
    { label: '2:1', ratio: 2 / 3 },
    { label: '3:1', ratio: 0.75 },
    { label: '1:0', ratio: 1 }
  ];

  const startSplitDrag = (ev: any) => {
    ev.preventDefault();
    const onMove = (moveEv: MouseEvent) => {
      const rect = splitWrapRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0) return;
      const next = (moveEv.clientX - rect.left) / rect.width;
      setSplitRatio(Math.max(0, Math.min(1, next)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const closeWorkspace = () => {
    setWorkspaceSession(null);
    setWorkspaceAssignmentId(null);
    setWorkspaceMode('onlyoffice');
    setIsDistractionFree(false);
  };

  const finalizeWorkspace = (assignmentId: number) => {
    setFinalizingEditorFor(assignmentId);
    finalizeStudentOnlyofficeSubmission(assignmentId)
      .then(() => {
        toast.success('OnlyOffice-Abgabe final eingereicht');
        closeWorkspace();
        assignmentsQuery.refetch();
        if (expandedId === assignmentId) void detailQuery.refetch();
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.error || 'Finale Einreichung fehlgeschlagen');
      })
      .finally(() => setFinalizingEditorFor(null));
  };

  const openSplitWorkspace = async (assignmentId: number, distractionFree = false) => {
    try {
      setOpeningEditorFor(assignmentId);
      const session = await openStudentOnlyofficeSession(assignmentId);
      setExpandedId(assignmentId);
      setWorkspaceAssignmentId(assignmentId);
      setWorkspaceMode('onlyoffice');
      setWorkspaceSession(session);
      setIsDistractionFree(distractionFree);
      setSplitRatio(0.5);
      toast.success('OnlyOffice-Arbeitsbereich geöffnet');
      assignmentsQuery.refetch();
      if (expandedId === assignmentId) void detailQuery.refetch();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Editor konnte nicht geöffnet werden');
    } finally {
      setOpeningEditorFor(null);
    }
  };

  const openTiptapWorkspace = (assignmentId: number, distractionFree = false) => {
    setExpandedId(assignmentId);
    setWorkspaceAssignmentId(assignmentId);
    setWorkspaceMode('tiptap');
    setWorkspaceSession(null);
    setIsDistractionFree(distractionFree);
    setSplitRatio(0.5);
  };

  const submitTiptapAssignment = async (assignment: any, html: string) => {
    const aid = Number(assignment?.assignment_id);
    if (!aid) return;
    const sebRequired = assignment?.seb_mode === 'seb_required';
    if (sebRequired && !getSebToken(aid)) {
      toast.error('Bitte SEB-Token eintragen');
      return;
    }
    const requestId =
      typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    const sanitizedTitle = String(assignment?.title || 'abgabe')
      .replace(/[^\w\-]+/g, '_')
      .slice(0, 40) || 'abgabe';
    const htmlDoc = `<!doctype html><html><head><meta charset="utf-8"><title>${sanitizedTitle}</title></head><body>${html || '<p></p>'}</body></html>`;
    const file = new File([htmlDoc], `${sanitizedTitle}.html`, { type: 'text/html' });
    try {
      setSubmittingTiptapFor(aid);
      await submitStudentAssignment({
        classworkId: Number(assignment.classwork_id),
        assignmentId: aid,
        file,
        source: 'tiptap',
        requestId,
        attemptToken: sebRequired ? getSebToken(aid) : undefined
      });
      toast.success('TipTap-Abgabe eingereicht');
      closeWorkspace();
      assignmentsQuery.refetch();
      if (expandedId === aid) void detailQuery.refetch();
    } catch (error) {
      const msg = (error as any)?.response?.data?.error || 'TipTap-Abgabe fehlgeschlagen';
      toast.error(msg);
    } finally {
      setSubmittingTiptapFor(null);
    }
  };

  const materialViewUrl = (classworkId: number, materialId: number) =>
    `/api/student/classwork/${classworkId}/materials/${materialId}/view`;
  const materialDownloadUrl = (classworkId: number, materialId: number) =>
    `/api/student/classwork/${classworkId}/materials/${materialId}/download`;

  const copyMaterialIntoEditor = async (assignment: any, material: any) => {
    if (!assignment || !material) return;
    const classworkId = Number(assignment.classwork_id);
    setCopyingMaterialId(Number(material.id));
    try {
      if (isExternalMaterial(material)) {
        const url = String(material.external_url || '').trim();
        await navigator.clipboard.writeText(url);
        toast.success('Link kopiert. Im Dokument mit Ctrl+V einfügen.');
        return;
      }
      if (isTextMaterial(material)) {
        const text = materialPreviewText || (await fetch(materialViewUrl(classworkId, Number(material.id)), { credentials: 'include' }).then((r) => r.text()));
        await navigator.clipboard.writeText(text || '');
        toast.success('Text kopiert. Im Dokument mit Ctrl+V einfügen.');
        return;
      }
      if (isImageMaterial(material)) {
        const blob = await fetch(materialViewUrl(classworkId, Number(material.id)), { credentials: 'include' }).then((r) => r.blob());
        const clipApi = navigator.clipboard as any;
        const ClipboardItemCtor = (window as any).ClipboardItem;
        if (clipApi?.write && ClipboardItemCtor) {
          await clipApi.write([new ClipboardItemCtor({ [blob.type || 'image/png']: blob })]);
          toast.success('Bild kopiert. Im Dokument mit Ctrl+V einfügen.');
          return;
        }
        await navigator.clipboard.writeText(materialDownloadUrl(classworkId, Number(material.id)));
        toast('Direktes Bild-Kopieren nicht verfügbar. Download-Link kopiert (Fallback: öffnen + Ctrl+C/Ctrl+V).');
        return;
      }
      await navigator.clipboard.writeText(materialDownloadUrl(classworkId, Number(material.id)));
      toast('Material-Link kopiert. Im Dokument mit Ctrl+V einfügen.');
    } catch {
      toast.error('Übernahme nicht automatisch möglich. Bitte manuell mit Ctrl+C/Ctrl+V.');
    } finally {
      setCopyingMaterialId(null);
    }
  };

  const renderWorkspaceMaterialsPane = (assignment: any, keyPrefix: string) => {
    const materials = workspaceMaterials;
    const picked = selectedWorkspaceMaterial;
    const selectedId = workspaceAssignmentId ? selectedMaterialIdByAssignment[workspaceAssignmentId] : null;
    return (
      <div className="p-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-gray-800">Aufgaben & Materialien</h4>
          {picked ? (
            <button
              type="button"
              className="btn-secondary text-xs"
              disabled={copyingMaterialId === Number(picked.id)}
              onClick={() => copyMaterialIntoEditor(assignment, picked)}
            >
              {copyingMaterialId === Number(picked.id) ? 'Übernehme …' : 'In Dokument übernehmen'}
            </button>
          ) : null}
        </div>
        {detailQuery.isLoading ? <p className="text-sm text-gray-500">Lade Materialien …</p> : null}
        {!detailQuery.isLoading && !materials.length ? <p className="text-sm text-gray-500">Keine Materialien vorhanden.</p> : null}
        {materials.length ? (
          <div className="grid gap-3 lg:grid-cols-[minmax(180px,260px)_1fr]">
            <div className="border border-gray-200 rounded-lg overflow-auto max-h-[56vh]">
              {materials.map((m: any) => {
                const mid = Number(m.id);
                const active = Number(selectedId) === mid;
                const external = isExternalMaterial(m);
                const viewUrl = external
                  ? String(m.external_url || '')
                  : materialViewUrl(Number(assignment.classwork_id), mid);
                return (
                  <div
                    key={`${keyPrefix}-material-${mid}`}
                    className={`flex items-center gap-1 px-2 py-1.5 border-b border-gray-100 last:border-b-0 ${active ? 'bg-primary-50' : 'bg-white hover:bg-gray-50'}`}
                  >
                    <button
                      type="button"
                      onClick={() => workspaceAssignmentId && setSelectedMaterialIdByAssignment((prev) => ({ ...prev, [workspaceAssignmentId]: mid }))}
                      className="flex-1 min-w-0 text-left px-1"
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">{m.title}</p>
                      <p className="text-xs text-gray-500">
                        {String(m.material_type || '') === 'aufgabenstellung' ? 'Aufgabenstellung' : 'Material'}
                        {external ? ' · Link' : ''}
                      </p>
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className={`${miniActionBtnClass} ${active ? 'bg-gray-900 text-white border-gray-900' : ''}`}
                        title={active ? 'Aktiv ausgewählt' : 'Vorschau anzeigen'}
                        onClick={() => workspaceAssignmentId && setSelectedMaterialIdByAssignment((prev) => ({ ...prev, [workspaceAssignmentId]: mid }))}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </button>
                      <a
                        className={miniActionBtnClass}
                        title="In neuem Tab öffnen"
                        href={viewUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                      <button
                        type="button"
                        className={miniActionBtnClass}
                        title="In Dokument übernehmen"
                        onClick={(e) => {
                          e.stopPropagation();
                          void copyMaterialIntoEditor(assignment, m);
                        }}
                        disabled={copyingMaterialId === mid}
                      >
                        <Clipboard className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border border-gray-200 rounded-lg bg-gray-50 min-h-[46vh] overflow-hidden">
              {!picked ? (
                <div className="h-full flex items-center justify-center text-sm text-gray-500">Material auswählen …</div>
              ) : isExternalMaterial(picked) ? (
                <div className="h-full">
                  <div className="px-3 py-2 border-b border-gray-200 bg-white text-xs">
                    <a className="text-primary-700 hover:underline" href={String(picked.external_url)} target="_blank" rel="noreferrer">Link in neuem Tab öffnen</a>
                  </div>
                  <iframe className="w-full h-[48vh] bg-white" src={String(picked.external_url)} title={`extern-${picked.id}`} />
                </div>
              ) : isImageMaterial(picked) ? (
                <div className="h-full flex flex-col">
                  <div className="px-3 py-2 border-b border-gray-200 bg-white text-xs">
                    <a className="text-primary-700 hover:underline" href={materialViewUrl(Number(assignment.classwork_id), Number(picked.id))} target="_blank" rel="noreferrer">Bild separat öffnen</a>
                  </div>
                  <div className="flex-1 overflow-auto p-3">
                    <img
                      src={materialViewUrl(Number(assignment.classwork_id), Number(picked.id))}
                      alt={String(picked.title || 'Material')}
                      className="max-w-full h-auto rounded border border-gray-200 bg-white"
                    />
                  </div>
                </div>
              ) : isPdfMaterial(picked) ? (
                <iframe
                  className="w-full h-[52vh] bg-white"
                  src={materialViewUrl(Number(assignment.classwork_id), Number(picked.id))}
                  title={`pdf-${picked.id}`}
                />
              ) : isTextMaterial(picked) ? (
                <div className="h-full bg-white">
                  {materialPreviewLoading ? (
                    <div className="h-full flex items-center justify-center text-sm text-gray-500">Text lädt …</div>
                  ) : materialPreviewError ? (
                    <div className="h-full flex items-center justify-center text-sm text-red-600">{materialPreviewError}</div>
                  ) : (
                    <pre className="whitespace-pre-wrap break-words text-sm text-gray-800 p-3 max-h-[52vh] overflow-auto">{materialPreviewText}</pre>
                  )}
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center p-4 text-center text-sm text-gray-600">
                  <p className="mb-2">Vorschau für diesen Dateityp ist eingeschränkt.</p>
                  <a
                    className="text-primary-700 hover:underline"
                    href={materialDownloadUrl(Number(assignment.classwork_id), Number(picked.id))}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Datei herunterladen
                  </a>
                </div>
              )}
            </div>
          </div>
        ) : null}
        {picked ? (
          <p className="text-xs text-gray-500">
            Hinweis: Wenn direkte Übernahme nicht möglich ist, öffne das Material und nutze Ctrl+C / Ctrl+V.
          </p>
        ) : null}
      </div>
    );
  };

  const renderWorkspaceEditorPane = (assignment: any) => {
    if (workspaceMode === 'tiptap') {
      const aid = Number(assignment?.assignment_id || workspaceAssignmentId || 0);
      return (
        <StudentTiptapEditorPane
          assignmentId={aid}
          readOnly={!canUpload(assignment)}
          isSubmitting={submittingTiptapFor === aid}
          onSubmit={(html) => {
            void submitTiptapAssignment(assignment, html);
          }}
        />
      );
    }
    return (
      <div className="relative h-full">
        {workspaceLoading ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">Editor lädt …</div>
        ) : null}
        {workspaceError ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-red-600 px-4 text-center">
            {workspaceError}
          </div>
        ) : null}
        <div id={workspaceEditorDivId} className="w-full h-full" style={{ display: workspaceLoading || workspaceError ? 'none' : 'block' }} />
      </div>
    );
  };

  const stats = useMemo(() => {
    const result = { open: 0, working: 0, submitted: 0, graded: 0 };
    list.forEach((row: any) => {
      const state = String(row.state || '').toLowerCase();
      if (['published', 'graded'].includes(state)) {
        result.graded += 1;
      } else if (['submitted', 'locked'].includes(state)) {
        result.submitted += 1;
      } else if (['started', 'resumed', 'in_progress'].includes(state)) {
        result.working += 1;
      } else {
        result.open += 1;
      }
    });
    return result;
  }, [list]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meine Klassenarbeiten</h1>
          <p className="text-sm text-gray-600 mt-1">
            Alles auf einen Blick: Status, Termine, Abgabe und veroeffentlichte Ergebnisse.
          </p>
        </div>
        <div className="text-xs text-gray-500">
          Insgesamt: <span className="font-semibold text-gray-700">{list.length}</span>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs uppercase tracking-wide text-slate-600">Noch nicht begonnen</p>
          <p className="text-xl font-bold text-slate-800">{stats.open}</p>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs uppercase tracking-wide text-amber-700">In Bearbeitung</p>
          <p className="text-xl font-bold text-amber-800">{stats.working}</p>
        </div>
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
          <p className="text-xs uppercase tracking-wide text-indigo-700">Abgegeben</p>
          <p className="text-xl font-bold text-indigo-800">{stats.submitted}</p>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-xs uppercase tracking-wide text-emerald-700">Bewertet</p>
          <p className="text-xl font-bold text-emerald-800">{stats.graded}</p>
        </div>
      </div>
      {nextSplitAssignment ? (
        <div className="rounded-xl border border-primary-200 bg-gradient-to-r from-primary-50 to-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Schnellstart</p>
              <p className="text-base font-semibold text-gray-900 mt-0.5">
                {Number(nextSplitAssignment.onlyoffice_enabled)
                  ? 'Nächste Klassenarbeit direkt im Splitscreen öffnen'
                  : 'Nächste Klassenarbeit im Editor öffnen'}
              </p>
              <p className="text-sm text-gray-600 mt-0.5 truncate">
                {nextSplitAssignment.title}
              </p>
            </div>
            <button
              type="button"
              className="btn-primary text-sm font-semibold px-6 py-3 shadow-md ring-2 ring-primary-200"
              disabled={openingEditorFor === Number(nextSplitAssignment.assignment_id)}
              onClick={() => {
                const aid = Number(nextSplitAssignment.assignment_id);
                if (Number(nextSplitAssignment.onlyoffice_enabled)) openSplitWorkspace(aid, true);
                else if (Number(nextSplitAssignment.tiptap_enabled)) openTiptapWorkspace(aid, true);
              }}
            >
              {openingEditorFor === Number(nextSplitAssignment.assignment_id)
                ? 'Öffne …'
                : Number(nextSplitAssignment.onlyoffice_enabled)
                  ? 'Jetzt im Splitscreen starten'
                  : 'Jetzt im Editor starten'}
            </button>
          </div>
        </div>
      ) : null}
      {urlSebToken ? (
        <p className="text-xs text-primary-800 bg-primary-50 border border-primary-100 rounded-md px-3 py-2">
          SEB-Token aus Link übernommen (wird bei Abgabe mitgeschickt).
        </p>
      ) : null}
      {!list.length && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-500">
          Aktuell sind keine Klassenarbeiten zugewiesen.
        </div>
      )}
      <div className="space-y-3">
        {list.map((assignment: any) => {
          const aid = Number(assignment.assignment_id);
          const sebRequired = assignment.seb_mode === 'seb_required';
          const expanded = expandedId === aid;
          const state = stateUi(assignment.state);
          const deadline = deadlineMeta(assignment.due_at, assignment.latest_submit_at);
          const publishedResult = formatPublishedResult(assignment);
          const startsAtLabel = formatDateTime(assignment.starts_at);
          const submittedAtLabel = formatDateTime(assignment.submitted_at);
          const resultPublishedAtLabel = formatDateTime(assignment.result_published_at);
          const canSubmit = canUpload(assignment);
          const onlyofficeEnabled = Boolean(Number(assignment.onlyoffice_enabled ?? 0));
          const tiptapEnabled = Boolean(Number(assignment.tiptap_enabled ?? 1));
          const hasAnyEditor = onlyofficeEnabled || tiptapEnabled;
          const fileUploadEnabled = Boolean(Number(assignment.student_file_upload_enabled ?? 0));
          const penpaperEnabled = Boolean(Number(assignment.penpaper_enabled ?? 0));
          const actionable = canSubmit && (hasAnyEditor || fileUploadEnabled || penpaperEnabled);
          const ctaLabel = ['started', 'resumed', 'in_progress'].includes(String(assignment.state || '').toLowerCase())
            ? 'Jetzt fortsetzen'
            : 'Jetzt starten';
          return (
            <div
              key={aid}
              className={`bg-white border rounded-xl p-4 shadow-sm ${
                deadline.isOverdue && actionable ? 'border-red-200' : 'border-gray-200'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-2 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-gray-900">{assignment.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${state.badge}`}>{state.label}</span>
                    {publishedResult ? (
                      <span className="text-xs px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-800 border-emerald-200">
                        Ergebnis veröffentlicht
                      </span>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <p className={deadline.toneClass}>{deadline.short}</p>
                    <p className="text-gray-600">{publishedResult ? `Ergebnis: ${publishedResult}` : 'Ergebnis: offen'}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  {actionable ? (
                    <button
                      type="button"
                      className="btn-primary text-sm font-semibold px-5 py-2.5 shadow-md ring-2 ring-primary-200"
                      onClick={() => {
                        if (onlyofficeEnabled) openSplitWorkspace(aid, true);
                        else if (tiptapEnabled) openTiptapWorkspace(aid, true);
                        else {
                          setExpandedId(aid);
                          toast('Bitte unten bei „Abgabe“ die gewünschte Form wählen.');
                        }
                      }}
                    >
                      {ctaLabel}
                    </button>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-sm font-medium">
                      Keine Bearbeitung möglich
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    onClick={() => setExpandedId(expanded ? null : aid)}
                  >
                    {expanded ? 'Details ausblenden' : 'Details anzeigen'}
                  </button>
                </div>
              </div>

              {expanded ? (
                <div className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-2">
                  {assignment.description ? (
                    <p className="text-sm text-gray-700">{assignment.description}</p>
                  ) : (
                    <p className="text-sm text-gray-500">Keine weitere Beschreibung hinterlegt.</p>
                  )}
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <p className={deadline.toneClass}>{deadline.label}</p>
                    <p className="text-gray-600">
                      {startsAtLabel ? `Anstehend am: ${startsAtLabel}` : 'Anstehend am: Noch nicht terminiert'}
                    </p>
                    <p className="text-gray-600">
                      Versuche: {assignment.attempt_count || 0} / {Math.max(1, Number(assignment.max_attempts) || 1)}
                    </p>
                    <p className="text-gray-600">SEB: {sebRequired ? 'erforderlich' : 'optional'}</p>
                    <p className="text-gray-600">
                      {submittedAtLabel ? `Geschrieben/abgegeben am: ${submittedAtLabel}` : 'Noch nicht abgegeben'}
                    </p>
                    <p className="text-gray-600">
                      {assignment.points_max != null ? `Max. Punkte: ${assignment.points_max}` : 'Max. Punkte: -'}
                    </p>
                  </div>
                  {publishedResult ? (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                      <p className="font-semibold">Ergebnis: {publishedResult}</p>
                      <p className="text-xs text-emerald-800 mt-0.5">
                        {resultPublishedAtLabel ? `Veröffentlicht am ${resultPublishedAtLabel}` : 'Bewertung ist veröffentlicht.'}
                      </p>
                      {assignment.result_feedback ? (
                        <p className="text-xs text-emerald-900 mt-1">Feedback: {assignment.result_feedback}</p>
                      ) : null}
                      {assignment.result_explanation_text ? (
                        <p className="text-xs text-emerald-900 mt-1 whitespace-pre-wrap">
                          Erklärung: {assignment.result_explanation_text}
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">Ergebnis: Noch nicht veröffentlicht.</p>
                  )}
                  {sebRequired ? (
                    <div className="pt-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">SEB-Versuch-Token (von Lehrkraft)</label>
                      <input
                        className="input-field text-sm"
                        placeholder="Token einfügen …"
                        value={getSebToken(aid)}
                        onChange={(e) => setSebTokenByAssignment((prev) => ({ ...prev, [aid]: e.target.value }))}
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              {expanded && detailQuery.isLoading ? <p className="text-sm text-gray-500 mt-2">Lade Details …</p> : null}
              {expanded && (detailQuery.data?.materials || []).filter((m: any) => !isTeacherOnlyMaterial(m)).length ? (
                <div className="mt-3 border-t border-gray-100 pt-3">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Bereitgestellte Materialien</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {(detailQuery.data.materials || [])
                      .filter((m: any) => !isTeacherOnlyMaterial(m))
                      .map((m: any) => (
                      <li key={m.id}>
                        {m.title}
                        {m.is_required ? <span className="text-amber-700"> (Pflicht)</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {expanded && detailQuery.data?.submissions?.length ? (
                <div className="mt-2 text-xs text-gray-500">
                  Abgaben:{' '}
                  {detailQuery.data.submissions.map((s: any) => (
                    <span key={s.id} className="mr-2">
                      #{s.attempt_no} {s.state}
                      {s.ocr_status ? ` (OCR: ${s.ocr_status})` : ''}
                      {s.analysis_status ? ` (Analyse: ${s.analysis_status})` : ''}
                    </span>
                  ))}
                </div>
              ) : null}
              {expanded && detailQuery.data?.submissions?.length ? (() => {
                const submissions = detailQuery.data.submissions as any[];
                const failedOnlyoffice = submissions
                  .filter((s) => String(s.source || '') === 'onlyoffice')
                  .find((s) => parseOnlyofficeError(s.metadata)?.status === 'failed');
                if (!failedOnlyoffice) return null;
                const info = parseOnlyofficeError(failedOnlyoffice.metadata);
                return (
                  <div className="mt-2 text-xs border border-red-200 bg-red-50 text-red-700 rounded-md px-2 py-2">
                    OnlyOffice-Speichern fehlgeschlagen.
                    {info?.lastErrorAt ? ` Letzter Fehler: ${new Date(info.lastErrorAt).toLocaleString()}.` : ''}
                    {' '}Bitte erneut im Editor oeffnen und speichern.
                  </div>
                );
              })() : null}
              {expanded && detailQuery.data?.submissions?.length ? (() => {
                const submissions = detailQuery.data.submissions as any[];
                const retryRequested = submissions
                  .filter((s) => String(s.source || '') === 'onlyoffice')
                  .find((s) => parseOnlyofficeRetry(s.metadata)?.status === 'retry_requested');
                if (!retryRequested) return null;
                const info = parseOnlyofficeRetry(retryRequested.metadata);
                return (
                  <div className="mt-2 text-xs border border-emerald-200 bg-emerald-50 text-emerald-800 rounded-md px-2 py-2">
                    Neuversuch wurde durch die Lehrkraft freigegeben.
                    {info?.retryRequestedAt ? ` Freigabe: ${new Date(info.retryRequestedAt).toLocaleString()}.` : ''}
                    {' '}Bitte OnlyOffice erneut oeffnen, speichern und final einreichen.
                  </div>
                );
              })() : null}

              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mr-1">Abgabe</span>
                {Number(assignment.onlyoffice_enabled) ? (
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    disabled={openingEditorFor === aid}
                    onClick={() => openSplitWorkspace(aid)}
                  >
                    {openingEditorFor === aid ? 'Öffne …' : canUpload(assignment) ? 'OnlyOffice öffnen' : 'OnlyOffice ansehen (read-only)'}
                  </button>
                ) : null}
                {tiptapEnabled ? (
                  <button
                    type="button"
                    className={`btn-secondary text-sm ${!canUpload(assignment) ? 'opacity-50 pointer-events-none' : ''}`}
                    disabled={!canUpload(assignment)}
                    onClick={() => openTiptapWorkspace(aid, true)}
                  >
                    TipTap Editor
                  </button>
                ) : null}
                {fileUploadEnabled ? (
                <label
                  className={`btn-primary cursor-pointer inline-block ${!canUpload(assignment) ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  Datei abgeben ({uploadModeLabel(studentUploadModeOf(assignment))})
                  <input
                    className="hidden"
                    type="file"
                    accept={uploadAcceptForMode(studentUploadModeOf(assignment))}
                    disabled={!canUpload(assignment)}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (sebRequired && !getSebToken(aid)) {
                        toast.error('Bitte SEB-Token eintragen');
                        e.target.value = '';
                        return;
                      }
                      const requestId =
                        typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
                      try {
                        setUploadingFor(aid);
                        await submitStudentAssignment({
                          classworkId: Number(assignment.classwork_id),
                          assignmentId: aid,
                          file,
                          source: 'upload',
                          requestId,
                          attemptToken: sebRequired ? getSebToken(aid) : undefined
                        });
                        toast.success('Abgabe hochgeladen');
                        assignmentsQuery.refetch();
                        if (expandedId === aid) void detailQuery.refetch();
                      } catch (error) {
                        const apiError = (error as any)?.response?.data?.error;
                        toast.error(apiError || 'Upload fehlgeschlagen');
                      } finally {
                        setUploadingFor(null);
                        e.target.value = '';
                      }
                    }}
                  />
                </label>
                ) : null}
                {uploadingFor === aid ? <span className="ml-2 text-xs text-primary-700">Upload läuft …</span> : null}
                {!canUpload(assignment) ? (
                  <p className="text-xs text-gray-500 mt-1">Abgabe nicht mehr möglich (Status oder keine Versuche mehr).</p>
                ) : null}
              </div>
              {Number(assignment.penpaper_enabled) ? (
                <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <p className="text-xs text-gray-700 mb-2">
                    <span className="font-semibold">So gibst du ab:</span> 1) Alle Seiten als Foto/Scan hochladen, 2) am Ende verbindlich abschicken.
                    Nach dem Abschicken kannst du diese Abgabe nicht weiter bearbeiten.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-semibold text-gray-700">Mehrseitige Foto-/Scan-Abgabe</span>
                    <label className={`btn-secondary text-xs cursor-pointer ${!canUpload(assignment) ? 'opacity-50 pointer-events-none' : ''}`}>
                      Abfotografieren
                      <input
                        className="hidden"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        disabled={!canUpload(assignment)}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            setUploadingPenpaperFor(aid);
                            await uploadStudentPenpaperPage(aid, file);
                            await refreshPenpaperPages(aid);
                            toast.success('Seite hinzugefügt');
                          } catch (error: any) {
                            toast.error(error?.response?.data?.error || 'Seite konnte nicht hochgeladen werden');
                          } finally {
                            setUploadingPenpaperFor(null);
                            e.target.value = '';
                          }
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      className={`btn-primary text-xs ${!canUpload(assignment) ? 'opacity-50 pointer-events-none' : ''}`}
                      disabled={!canUpload(assignment) || finalizingPenpaperFor === aid}
                      onClick={async () => {
                        try {
                          setFinalizingPenpaperFor(aid);
                          await finalizeStudentPenpaperSubmission(aid);
                          toast.success('Abgabe verbindlich abgeschickt');
                          assignmentsQuery.refetch();
                          if (expandedId === aid) {
                            void detailQuery.refetch();
                            await refreshPenpaperPages(aid);
                          }
                        } catch (error: any) {
                          const apiErr = error?.response?.data;
                          const failedPageNos = Array.isArray(apiErr?.data?.failedPageNos)
                            ? apiErr.data.failedPageNos.map((n: any) => Number(n)).filter((n: number) => Number.isFinite(n))
                            : [];
                          const firstReason = Array.isArray(apiErr?.data?.failedPages)
                            ? String(apiErr.data.failedPages?.[0]?.quality?.failReasons?.[0] || '').trim()
                            : '';
                          await refreshPenpaperPages(aid);
                          if (failedPageNos.length > 0) {
                            toast.error(
                              `Einige Seiten sind noch nicht gut lesbar (Seite ${failedPageNos.join(', ')}). ${firstReason ? `Grund: ${firstReason}. ` : ''}Bitte Seite neu fotografieren und erneut hochladen.`
                            );
                          } else {
                            toast.error(apiErr?.error || 'Abgabe konnte nicht abgeschickt werden. Bitte prüfe deine Seiten und versuche es erneut.');
                          }
                        } finally {
                          setFinalizingPenpaperFor(null);
                        }
                      }}
                    >
                      {finalizingPenpaperFor === aid ? 'Sende ab …' : 'Abgabe verbindlich abschicken'}
                    </button>
                    {uploadingPenpaperFor === aid ? <span className="text-xs text-primary-700">Upload läuft …</span> : null}
                  </div>
                  {Array.isArray(penpaperPagesByAssignment[aid]) && penpaperPagesByAssignment[aid].length ? (
                    <div className="mt-2 overflow-auto">
                      <table className="min-w-full text-xs">
                        <thead className="text-gray-600">
                          <tr>
                            <th className="text-left py-1 pr-2">Seite</th>
                            <th className="text-left py-1 pr-2">Vollständigkeit</th>
                            <th className="text-left py-1 pr-2">Analyse</th>
                            <th className="text-left py-1">Aktion</th>
                          </tr>
                        </thead>
                        <tbody>
                          {penpaperPagesByAssignment[aid].map((p: any) => (
                            <tr key={p.id} className="border-t border-gray-200">
                              <td className="py-1 pr-2">{p.page_no}</td>
                              <td className={`py-1 pr-2 ${String(p.completeness_status) === 'pass' ? 'text-emerald-700' : 'text-rose-700'}`}>
                                {String(p.completeness_status || 'pending') === 'pass'
                                  ? 'OK'
                                  : String(p.completeness_status || 'pending') === 'pending'
                                    ? 'Prüfung läuft'
                                    : 'Bitte prüfen'}
                              </td>
                              <td className="py-1 pr-2">
                                {String(p.analysis_status || 'pending') === 'completed'
                                  ? 'Fertig'
                                  : String(p.analysis_status || 'pending') === 'running'
                                    ? 'Läuft'
                                    : String(p.analysis_status || 'pending') === 'failed'
                                      ? 'Fehler'
                                      : 'Ausstehend'}
                              </td>
                              <td className="py-1">
                                <button
                                  type="button"
                                  className="text-xs text-rose-700 hover:underline"
                                  onClick={async () => {
                                    try {
                                      await deleteStudentPenpaperPage(aid, Number(p.page_no));
                                      await refreshPenpaperPages(aid);
                                    } catch {
                                      toast.error('Seite konnte nicht entfernt werden');
                                    }
                                  }}
                                >
                                  Entfernen
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-gray-600">Noch keine Seiten hochgeladen.</p>
                  )}
                </div>
              ) : null}
              {workspaceAssignmentId === aid && (workspaceMode === 'tiptap' || workspaceSession) && !isDistractionFree ? (
                <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden bg-white">
                  <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-gray-700">Split-Preset:</span>
                      {splitPresets.map((p) => (
                        <button
                          key={p.label}
                          type="button"
                          className={`px-2 py-1 text-xs rounded border ${
                            Math.abs(splitRatio - p.ratio) < 0.02
                              ? 'bg-primary-600 border-primary-600 text-white'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                          }`}
                          onClick={() => setSplitRatio(p.ratio)}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      {workspaceMode === 'onlyoffice' ? (
                        <button
                          type="button"
                          className="btn-primary text-xs"
                          disabled={finalizingEditorFor === aid}
                          onClick={() => finalizeWorkspace(aid)}
                        >
                          {finalizingEditorFor === aid ? 'Reiche ein …' : 'Final einreichen'}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn-secondary text-xs"
                        onClick={closeWorkspace}
                      >
                        Schließen
                      </button>
                    </div>
                  </div>
                  <div ref={splitWrapRef} className="relative h-[70vh] w-full flex bg-gray-100">
                    <div className="h-full min-w-0 bg-white" style={{ width: `${Math.max(0, Math.min(1, splitRatio)) * 100}%` }}>
                      {splitRatio <= 0 ? null : renderWorkspaceEditorPane(assignment)}
                    </div>
                    <div
                      role="separator"
                      aria-label="Split-Steg"
                      className="w-2 cursor-col-resize bg-gray-300 hover:bg-primary-400 transition-colors"
                      onMouseDown={startSplitDrag}
                    />
                    <div
                      className="h-full min-w-0 overflow-auto bg-white border-l border-gray-200"
                      style={{ width: `${Math.max(0, Math.min(1, 1 - splitRatio)) * 100}%` }}
                    >
                      {renderWorkspaceMaterialsPane(assignment, `inline-${aid}`)}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {isDistractionFree && workspaceAssignmentId && activeWorkspaceAssignment && (workspaceMode === 'tiptap' || workspaceSession) ? (
        <div className="fixed inset-0 z-[100] bg-white">
          <div className="h-full w-full flex flex-col">
            <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Fokusmodus</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{activeWorkspaceAssignment.title}</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex flex-wrap items-center gap-1">
                  <span className="text-xs font-medium text-gray-700 mr-1">Split:</span>
                  {splitPresets.map((p) => (
                    <button
                      key={`focus-${p.label}`}
                      type="button"
                      className={`px-2 py-1 text-xs rounded border ${
                        Math.abs(splitRatio - p.ratio) < 0.02
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                      onClick={() => setSplitRatio(p.ratio)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {workspaceMode === 'onlyoffice' ? (
                  <button
                    type="button"
                    className="btn-primary text-xs"
                    disabled={finalizingEditorFor === workspaceAssignmentId}
                    onClick={() => finalizeWorkspace(Number(workspaceAssignmentId))}
                  >
                    {finalizingEditorFor === workspaceAssignmentId ? 'Reiche ein …' : 'Final einreichen'}
                  </button>
                ) : null}
                <button type="button" className="btn-secondary text-xs" onClick={closeWorkspace}>
                  Fokusmodus schließen
                </button>
              </div>
            </div>
            <div ref={splitWrapRef} className="relative flex-1 min-h-0 w-full flex bg-gray-100">
              <div className="h-full min-w-0 bg-white" style={{ width: `${Math.max(0, Math.min(1, splitRatio)) * 100}%` }}>
                {splitRatio <= 0 ? null : renderWorkspaceEditorPane(activeWorkspaceAssignment)}
              </div>
              <div
                role="separator"
                aria-label="Split-Steg"
                className="w-2 cursor-col-resize bg-gray-300 hover:bg-primary-400 transition-colors"
                onMouseDown={startSplitDrag}
              />
              <div
                className="h-full min-w-0 overflow-auto bg-white border-l border-gray-200"
                style={{ width: `${Math.max(0, Math.min(1, 1 - splitRatio)) * 100}%` }}
              >
                {renderWorkspaceMaterialsPane(activeWorkspaceAssignment, 'focus')}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
